import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import QuickActionButton from '../../components/ui/QuickActionButton';
import { NotificationProvider, useNotification } from '../../components/ui/NotificationToast';
import Button from '../../components/ui/Button';
import { subUnionsService } from '../../services/dataService';


// Import form components
import BasicInfoSection from './components/BasicInfoSection';
import DescriptionSection from './components/DescriptionSection';
import MemberManagementSection from './components/MemberManagementSection';
import FormProgress from './components/FormProgress';
import PreviewModal from './components/PreviewModal';

const SubUnionFormContent = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const subUnionId = searchParams.get('id');
  const { success, error, warning } = useNotification();

  // UI State
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Form Data State
  const [formData, setFormData] = useState({
    // Basic Information
    nameAr: '',
    nameEn: '',
    nameTr: '',
    cityId: '',
    status: 'active',
    logo: '',
    establishedDate: '',
    registrationNumber: '',
    phone: '',
    contactEmail: '',
    contactNumberAr: '',
    contactNumberEn: '',
    contactNumberTr: '',

    // Descriptions
    descriptionAr: '',
    descriptionEn: '',
    descriptionTr: '',
    cardDescriptionAr: '',
    cardDescriptionEn: '',
    cardDescriptionTr: '',
    aboutDescriptionAr: '',
    aboutDescriptionEn: '',
    aboutDescriptionTr: '',

    // Member Management
    totalMembers: '',
    activeMembers: '',
    newMembersThisMonth: '',
    contacts: [],

    // Additional Content
    specialAchievementsAr: '',
    specialAchievementsEn: '',
    specialAchievementsTr: '',
    activitiesAndAchievementsAr: '',
    activitiesAndAchievementsEn: '',
    activitiesAndAchievementsTr: '',
    currentAdministrativeTeamAr: '',
    currentAdministrativeTeamEn: '',
    currentAdministrativeTeamTr: '',
    notesAr: '',
    notesEn: '',
    notesTr: '',

    // Additional Settings
    website: '',
    facebookPage: '',
    allowNewRegistrations: true,
    emailNotifications: true,
    showInDirectory: true
  });

  // Form Validation State
  const [errors, setErrors] = useState({});

  const totalSteps = 2;

  // Load existing data if editing
  useEffect(() => {
    const loadSubUnion = async () => {
      if (!subUnionId) return;

      setIsLoading(true);
      try {
        const subUnion = await subUnionsService.getById(subUnionId);
        
        // Extract year from established_year if it exists
        const establishedDate = subUnion?.establishedYear 
          ? `${subUnion.establishedYear}-01-01` 
          : '';

        // Get primary contact from contacts if available
        const primaryContact = subUnion?.contacts?.[0] || null;

        setFormData({
          nameAr: subUnion?.nameAr || '',
          nameEn: subUnion?.nameEn || '',
          nameTr: subUnion?.nameTr || '',
          cityId: subUnion?.cityId?.toString() || '',
          status: subUnion?.status || 'active',
          logo: subUnion?.logoUrl || '',
          establishedDate: establishedDate,
          registrationNumber: '',
          phone: subUnion?.phone || primaryContact?.phone || '',
          contactEmail: subUnion?.contactEmail || primaryContact?.email || '',
          contactNumberAr: subUnion?.contactNumberAr || '',
          contactNumberEn: subUnion?.contactNumberEn || '',
          contactNumberTr: subUnion?.contactNumberTr || '',
          descriptionAr: subUnion?.aboutDescriptionAr || subUnion?.cardDescriptionAr || '',
          descriptionEn: subUnion?.aboutDescriptionEn || subUnion?.cardDescriptionEn || '',
          descriptionTr: subUnion?.aboutDescriptionTr || subUnion?.cardDescriptionTr || '',
          cardDescriptionAr: subUnion?.cardDescriptionAr || '',
          cardDescriptionEn: subUnion?.cardDescriptionEn || '',
          cardDescriptionTr: subUnion?.cardDescriptionTr || '',
          aboutDescriptionAr: subUnion?.aboutDescriptionAr || '',
          aboutDescriptionEn: subUnion?.aboutDescriptionEn || '',
          aboutDescriptionTr: subUnion?.aboutDescriptionTr || '',
          totalMembers: subUnion?.memberCount?.toString() || '0',
          activeMembers: '',
          newMembersThisMonth: '',
          contacts: primaryContact ? [{
            id: 1,
            name: primaryContact?.name || '',
            position: '',
            email: subUnion?.contactEmail || primaryContact?.email || '',
            phone: subUnion?.phone || primaryContact?.phone || '',
            isPrimary: true
          }] : [],
          specialAchievementsAr: subUnion?.specialAchievementsAr || '',
          specialAchievementsEn: subUnion?.specialAchievementsEn || '',
          specialAchievementsTr: subUnion?.specialAchievementsTr || '',
          activitiesAndAchievementsAr: subUnion?.activitiesAndAchievementsAr || '',
          activitiesAndAchievementsEn: subUnion?.activitiesAndAchievementsEn || '',
          activitiesAndAchievementsTr: subUnion?.activitiesAndAchievementsTr || '',
          currentAdministrativeTeamAr: subUnion?.currentAdministrativeTeamAr || '',
          currentAdministrativeTeamEn: subUnion?.currentAdministrativeTeamEn || '',
          currentAdministrativeTeamTr: subUnion?.currentAdministrativeTeamTr || '',
          notesAr: subUnion?.notesAr || '',
          notesEn: subUnion?.notesEn || '',
          notesTr: subUnion?.notesTr || '',
          website: '',
          facebookPage: '',
          allowNewRegistrations: true,
          emailNotifications: true,
          showInDirectory: true
        });

        setLastSaved(new Date());
        setHasUnsavedChanges(false);
      } catch (err) {
        console.error('Error loading sub-union:', err);
        error('فشل تحميل بيانات الاتحاد الفرعي');
      } finally {
        setIsLoading(false);
      }
    };

    loadSubUnion();
  }, [subUnionId]);

  // Auto-save functionality
  useEffect(() => {
    if (hasUnsavedChanges) {
      const autoSaveTimer = setTimeout(() => {
        handleAutoSave();
      }, 30000); // Auto-save every 30 seconds

      return () => clearTimeout(autoSaveTimer);
    }
  }, [formData, hasUnsavedChanges]);

  // Handle form data updates
  const handleFormUpdate = (updates) => {
    setFormData((prev) => ({ ...prev, ...updates }));
    setHasUnsavedChanges(true);

    // Clear related errors
    const updatedErrors = { ...errors };
    Object.keys(updates)?.forEach((key) => {
      if (updatedErrors?.[key]) {
        delete updatedErrors?.[key];
      }
    });
    setErrors(updatedErrors);
  };

  // Validation function
  const validateForm = (step = null) => {
    const newErrors = {};

    // Basic Information validation
    if (!step || step === 1) {
      if (!formData?.nameAr?.trim()) {
        newErrors.nameAr = 'الاسم بالعربية مطلوب';
      }
      if (!formData?.nameEn?.trim()) {
        newErrors.nameEn = 'الاسم بالإنجليزية مطلوب';
      }
      if (!formData?.cityId) {
        newErrors.cityId = 'المدينة مطلوبة';
      }
      // Status validation removed per user request
      // if (!formData?.status) {
      //   newErrors.status = 'الحالة مطلوبة';
      // }
    }

    // Description validation
    if (!step || step === 2) {
      if (!formData?.descriptionAr?.trim()) {
        newErrors.descriptionAr = 'الوصف بالعربية مطلوب';
      }
      // Member Management validation (moved to step 2)
      if (!formData?.totalMembers || parseInt(formData?.totalMembers) < 0) {
        newErrors.totalMembers = 'عدد الأعضاء الإجمالي مطلوب';
      }
      if (formData?.activeMembers && parseInt(formData?.activeMembers) > parseInt(formData?.totalMembers)) {
        newErrors.activeMembers = 'عدد الأعضاء النشطين لا يمكن أن يكون أكبر من العدد الإجمالي';
      }
    }

    // Email validation for contacts
    formData?.contacts?.forEach((contact, index) => {
      if (contact?.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(contact?.email)) {
        newErrors[`contact_${index}_email`] = 'صيغة البريد الإلكتروني غير صحيحة';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const transformSubUnionData = () => {
    // Extract year from establishedDate
    const establishedYear = formData?.establishedDate 
      ? new Date(formData.establishedDate).getFullYear()
      : null;

    // Get primary contact
    const primaryContact = formData?.contacts?.find(c => c?.isPrimary) || formData?.contacts?.[0];

    return {
      nameAr: formData?.nameAr || '',
      nameEn: formData?.nameEn || '',
      nameTr: formData?.nameTr || '',
      phone: formData?.phone || primaryContact?.phone || formData?.contacts?.[0]?.phone || '',
      contactEmail: formData?.contactEmail || primaryContact?.email || formData?.contacts?.[0]?.email || '',
      cityId: formData?.cityId ? parseInt(formData.cityId) : null,
      status: formData?.status || 'active',
      memberCount: parseInt(formData?.totalMembers) || 0,
      establishedYear: establishedYear,
      logoUrl: formData?.logo || '',
      contactNumberAr: formData?.contactNumberAr || primaryContact?.phone || '',
      contactNumberEn: formData?.contactNumberEn || primaryContact?.phone || '',
      contactNumberTr: formData?.contactNumberTr || primaryContact?.phone || '',
      cardDescriptionAr: formData?.cardDescriptionAr || '',
      cardDescriptionEn: formData?.cardDescriptionEn || '',
      cardDescriptionTr: formData?.cardDescriptionTr || '',
      aboutDescriptionAr: formData?.aboutDescriptionAr || '',
      aboutDescriptionEn: formData?.aboutDescriptionEn || '',
      aboutDescriptionTr: formData?.aboutDescriptionTr || '',
      specialAchievementsAr: formData?.specialAchievementsAr || '',
      specialAchievementsEn: formData?.specialAchievementsEn || '',
      specialAchievementsTr: formData?.specialAchievementsTr || '',
      activitiesAndAchievementsAr: formData?.activitiesAndAchievementsAr || '',
      activitiesAndAchievementsEn: formData?.activitiesAndAchievementsEn || '',
      activitiesAndAchievementsTr: formData?.activitiesAndAchievementsTr || '',
      currentAdministrativeTeamAr: formData?.currentAdministrativeTeamAr || '',
      currentAdministrativeTeamEn: formData?.currentAdministrativeTeamEn || '',
      currentAdministrativeTeamTr: formData?.currentAdministrativeTeamTr || '',
      notesAr: formData?.notesAr || '',
      notesEn: formData?.notesEn || '',
      notesTr: formData?.notesTr || ''
    };
  };

  // Auto-save function
  const handleAutoSave = async () => {
    if (!hasUnsavedChanges) return;

    try {
      const subUnionData = transformSubUnionData();
      
      if (subUnionId) {
        // Update existing sub-union
        await subUnionsService.update(subUnionId, subUnionData);
      } else {
        // Create new sub-union as draft
        const newSubUnion = await subUnionsService.create({
          ...subUnionData,
          status: 'active' // Auto-save as active
        });
        
        // Update URL with new sub-union ID
        window.history.replaceState({}, '', `/sub-union-form?id=${newSubUnion?.id}`);
      }
      
      setLastSaved(new Date());
      setHasUnsavedChanges(false);
    } catch (err) {
      console.error('Auto-save failed:', err);
    }
  };

  // Manual save function
  const handleSave = async () => {
    setIsSaving(true);

    try {
      // Validate current step
      if (!validateForm()) {
        error('يرجى تصحيح الأخطاء قبل الحفظ');
        setIsSaving(false);
        return;
      }

      const subUnionData = transformSubUnionData();
      
      let savedSubUnion;
      if (subUnionId) {
        // Update existing sub-union
        savedSubUnion = await subUnionsService.update(subUnionId, subUnionData);
      } else {
        // Create new sub-union
        savedSubUnion = await subUnionsService.create(subUnionData);
        
        // Update URL with new sub-union ID
        window.history.replaceState({}, '', `/sub-union-form?id=${savedSubUnion?.id}`);
      }

      setLastSaved(new Date());
      setHasUnsavedChanges(false);
      success('تم حفظ البيانات بنجاح');

    } catch (err) {
      console.error('Error saving sub-union:', err);
      error('حدث خطأ أثناء حفظ البيانات');
    } finally {
      setIsSaving(false);
    }
  };

  // Preview function
  const handlePreview = () => {
    if (!validateForm()) {
      error('يرجى تصحيح الأخطاء قبل المعاينة');
      return;
    }
    setIsPreviewOpen(true);
  };

  // Publish function
  const handlePublish = async () => {
    if (!validateForm()) {
      error('يرجى تصحيح جميع الأخطاء قبل النشر');
      return;
    }

    try {
      setIsSaving(true);

      const subUnionData = transformSubUnionData();
      subUnionData.status = 'active'; // Set as active when publishing
      
      let savedSubUnion;
      if (subUnionId) {
        // Update existing sub-union
        savedSubUnion = await subUnionsService.update(subUnionId, subUnionData);
      } else {
        // Create new sub-union
        savedSubUnion = await subUnionsService.create(subUnionData);
        
        // Update URL with new sub-union ID
        window.history.replaceState({}, '', `/sub-union-form?id=${savedSubUnion?.id}`);
      }

      success('تم نشر الاتحاد الفرعي بنجاح');
      setHasUnsavedChanges(false);

      // Navigate back to sub-unions list
      setTimeout(() => {
        navigate('/sub-unions-management');
      }, 1500);

    } catch (err) {
      console.error('Error publishing sub-union:', err);
      error('حدث خطأ أثناء نشر الاتحاد الفرعي');
    } finally {
      setIsSaving(false);
    }
  };

  // Step navigation
  const handleStepChange = (step) => {
    if (step < 1 || step > totalSteps) return;

    // Validate current step before moving
    if (step > currentStep && !validateForm(currentStep)) {
      error('يرجى تصحيح الأخطاء في الخطوة الحالية');
      return;
    }

    setCurrentStep(step);
  };

  // Handle navigation away with unsaved changes
  const handleNavigation = (path) => {
    if (hasUnsavedChanges) {
      const confirmLeave = window.confirm('لديك تغييرات غير محفوظة. هل تريد المغادرة؟');
      if (!confirmLeave) return;
    }
    navigate(path);
  };

  // Render current step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <BasicInfoSection
            formData={formData}
            onUpdate={handleFormUpdate}
            errors={errors} />);


      case 2:
        return (
          <DescriptionSection
            formData={formData}
            onUpdate={handleFormUpdate}
            errors={errors} />);


      case 3:
        return (
          <MemberManagementSection
            formData={formData}
            onUpdate={handleFormUpdate}
            errors={errors} />);


      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">جاري تحميل بيانات الاتحاد الفرعي...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <Header
        onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        isMenuOpen={isMobileMenuOpen} />

      {/* Sidebar */}
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)} />

      {/* Main Content */}
      <main className={`transition-all duration-300 ${
      isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'} pt-16`
      }>
        <div className="p-6">
          {/* Page Header */}
          <div className="mb-6">
            <Breadcrumbs />
            
            <div className="flex items-center justify-between mt-4">
              <div>
                <h1 className="text-2xl font-bold text-foreground font-arabic">
                  {formData?.nameAr ? `تحرير: ${formData?.nameAr}` : 'إنشاء اتحاد فرعي جديد'}
                </h1>
                <p className="text-muted-foreground mt-1 font-caption">
                  {formData?.nameAr ? 'تحرير بيانات الاتحاد الفرعي' : 'أضف اتحاد فرعي جديد إلى النظام'}
                </p>
              </div>

              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  onClick={() => handleNavigation('/sub-unions-management')}
                  iconName="ArrowLeft"
                  iconPosition="left"
                  iconSize={16}>

                  العودة للقائمة
                </Button>
                
                <Button
                  variant="secondary"
                  onClick={handlePreview}
                  iconName="Eye"
                  iconPosition="left"
                  iconSize={16}>

                  معاينة
                </Button>
              </div>
            </div>
          </div>

          {/* Form Layout */}
          <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
            {/* Form Content */}
            <div className="xl:col-span-3">
              <div className="bg-card border border-border rounded-lg p-6">
                {renderStepContent()}
              </div>
            </div>

            {/* Progress Sidebar */}
            <div className="xl:col-span-1">
              <div className="sticky top-24">
                <FormProgress
                  currentStep={currentStep}
                  totalSteps={totalSteps}
                  onStepChange={handleStepChange}
                  onSave={handleSave}
                  onPreview={handlePreview}
                  onPublish={handlePublish}
                  isSaving={isSaving}
                  hasUnsavedChanges={hasUnsavedChanges}
                  formData={formData}
                  errors={errors} />

              </div>
            </div>
          </div>
        </div>
      </main>
      {/* Preview Modal */}
      <PreviewModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        formData={formData} />

      {/* Quick Action Button */}
      <QuickActionButton />
    </div>);

};

const SubUnionForm = () => {
  return (
    <NotificationProvider>
      <SubUnionFormContent />
    </NotificationProvider>);

};

export default SubUnionForm;